import React, { useState, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { KeywordTab } from './tab';
import { TABLIST } from './constants/index';
import { KeywordRanking } from './keyword-ranking';
import { SearchInputStackContainer } from './search-input-stack-container';
import { KeywordGroups } from './keyword-groups';
import { getKeywordPlannerStyles } from './style/keyword-planner-style';
import { ActionBar } from './action-bar';
import { NoData } from './no-data';

export const KeywordPlanner = ({
  targetedLocationInfo,
  supportedLanguages,
  onClose,
}) => {
  const activity = useContext(ActivityContext);
  const classes = useDefaultStyles(getKeywordPlannerStyles);
  const i18n = useContext(I18nContext);
  const [selectedTab, setSelectedTab] = useState(TABLIST.GROUP);
  const [blocking, setBlocking] = useState(false);
  // allData:
  //  null: no input, no search result; doesn't show anything
  //  []: empty search result, show warning message
  //  [{...}]: search result, show keyword grouping and ranking
  const [allData, setAllData] = useState(null);
  const [selectedData, setSelectedData] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    Permissions,
    DynamicPermissions,
  } = useContext(ServerContext);
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const onTabSelect = useCallback((_e, tabData) => {
    setSelectedTab(tabData.value);
    activity.signal(`onSwitchSidePanelTab - ${JSON.stringify({
      Tab: tabData.value,
    })}`);
  }, [activity]);
  const onRecommendationSuccess = useCallback(({ data, traceInfo }) => {
    if (data === null) { // data null means no request sent
      return;
    }

    let dataWithKey = [];
    if (permissions && permissions.dynamic && permissions.dynamic.KeywordPlannerLiteRefactorPhase1 && data && _.first(data.value)) {
      const keywordAggregations = _.first(data.value).Aggregations || [];
      dataWithKey = _.map(keywordAggregations, group => ({
        ...group,
        Suggestions: _.map(group.Keywords, (keyword) => {
          const keywordText = keyword.Keyword ? keyword.Keyword.Text : '';
          return {
            ...keyword,
            AggregateMetric: {
              AverageSearchVolume: _.property(['Idea', 'AvgSearchs'])(_.find(_.first(data.value).Keywords, item => item.Keyword && item.Keyword.Text === keywordText)) || 0,
            },
            Keyword: keywordText,
            key: group.Name + keywordText,
            RequestId: traceInfo && traceInfo.requestId,
          };
        }),
      }));
    } else {
      dataWithKey = _.map(data.value, group => ({
        ...group,
        Suggestions: _.map(group.Suggestions, keyword => ({
          ...keyword,
          key: keyword.Tag + keyword.Keyword,
        })),
      }));
    }
    activity.signal(`onFetchRecommendationInSidePanel - ${_.isEmpty(dataWithKey) ? 'no data' : 'has data'}`);
    setAllData(dataWithKey);
    setSelectedData([]);
    setRefreshKey(refreshKey + 1);
  }, [refreshKey, activity, permissions]);
  const unblockPanel = useCallback(() => {
    setBlocking(false);
  }, []);
  const blockPanel = useCallback(() => {
    setBlocking(true);
  }, []);
  const onSelectChange = useCallback((data) => {
    setSelectedData(data);
  }, []);
  const onAdd = useCallback(() => {
    onClose(selectedData, selectedTab, activity);
  }, [onClose, selectedData, selectedTab, activity]);
  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const allRankingData = useMemo(() => _.flatten(_.pluck(allData, 'Suggestions')), [allData]);

  return (
    <UiBlocker
      blocking={blocking}
      loadingText={i18n.getString('Loading')}
    >
      <div className={`keyword-planner ${classes.flexContainer}`}>
        <SearchInputStackContainer
          beforeSearch={blockPanel}
          onSearchSuccess={onRecommendationSuccess}
          onSearchFinally={unblockPanel}
          targetedLocationInfo={targetedLocationInfo}
          supportedLanguages={supportedLanguages}
        />
        <NoData data={allData} />
        {
            !_.isEmpty(allData) && (
              <>
                <KeywordTab selectedTab={selectedTab} onTabSelect={onTabSelect} />
                <div className={classes.dataContainer} >
                  {
                    selectedTab === TABLIST.GROUP &&
                    <KeywordGroups
                      key={refreshKey}
                      allData={allData}
                      selectedData={selectedData}
                      onSelectChange={onSelectChange}
                    />
                  }
                  {
                    selectedTab === TABLIST.RANK &&
                    <div>
                      <KeywordRanking
                        allData={allRankingData}
                        selectedData={selectedData}
                        onSelectChange={onSelectChange}
                        key={refreshKey} // to force re-render
                      />
                    </div>
                  }
                </div>
              </>
            )
        }
        <ActionBar
          onAdd={onAdd}
          onCancel={onCancel}
        />
      </div>
    </UiBlocker>
  );
};

KeywordPlanner.propTypes = {
  targetedLocationInfo: PropTypes.shape({}).isRequired,
  supportedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
};
